import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { myEmail, canEditTask } from '../lib/taskAccess';
import { dueBucket } from '../lib/taskDue.mjs';
import { ACTIVE_TASK_STATUSES, isClosedTask } from '../lib/taskStatus';
import { subjectChoices } from '../lib/taskSubjects.mjs';
import {
  Plus, X, Check, RotateCcw, SkipForward, Users, Building2, Crosshair, UserRound, ExternalLink,
} from 'lucide-react';

// Centralized task hub — every CRM task in one place, grouped by due date, with
// create / status / reassign / re-open and a per-task history timeline.
// Status model (increment 2): open | in_progress | on_hold (active) + done | skipped (closed).

const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'done', label: 'Completed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'all', label: 'All' },
];
// Filters that show outstanding work → grouped by due date. Closed/all views are flat.
const GROUPED_FILTERS = ['active', 'open', 'in_progress', 'on_hold'];

const STATUS_META = {
  open:        { label: 'Open',        cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  on_hold:     { label: 'On Hold',     cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  done:        { label: 'Done',        cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  skipped:     { label: 'Skipped',     cls: 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400' },
};
const statusLabel = (s) => STATUS_META[s]?.label || s || '—';

// One-line description of a task history event for the timeline.
function describeEvent(ev) {
  switch (ev.type) {
    case 'created': return 'Created';
    case 'status_change': return `${statusLabel(ev.from_value)} → ${statusLabel(ev.to_value)}`;
    case 'reassigned': return `Reassigned: ${ev.from_value || '—'} → ${ev.to_value || '—'}`;
    case 'reopened': return `Re-opened${ev.note ? `: ${ev.note}` : ''}`;
    case 'edited': return 'Edited';
    default: return ev.type;
  }
}

const input =
  'rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

// Groups read the picked date against the partners' Eastern today.
function bucketOf(task, now) {
  return dueBucket(task.due_at, now);
}

const BUCKET_META = {
  overdue: { label: 'Overdue', tone: 'text-red-600' },
  today: { label: 'Today', tone: 'text-amber-600' },
  week: { label: 'This week', tone: 'text-indigo-600' },
  later: { label: 'Later', tone: 'text-[var(--text-secondary)]' },
  none: { label: 'No due date', tone: 'text-[var(--text-secondary)]' },
};
const BUCKET_ORDER = ['overdue', 'today', 'week', 'later', 'none'];

// Format a date-only due_at without the local-timezone off-by-one (UTC midnight would
// render as the prior evening for ET users).
function fmtDue(due) {
  if (!due) return null;
  return new Date(due).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' });
}

export default function TasksPage() {
  const navigate = useNavigate();
  const me = useMemo(() => myEmail(), []);
  // Edit rights mirror the server (assignee / creator / admin) — used to gate row controls.
  const editable = canEditTask;

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('active');
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all' | 'me' | <email>
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);  // task object | 'new' | null
  const [reopening, setReopening] = useState(null); // task being re-opened
  const [refresh, setRefresh] = useState(0);      // bump to re-run loadTasks with CURRENT filters
  const reload = useCallback(() => setRefresh((n) => n + 1), []);

  // Reference data for subject names + the new-task picker — loaded once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const safe = (p) => p.then((r) => r).catch(() => ({}));
      // One tasks-module call for the subject names/picker (works for task-only users
      // who have no CRM read), plus the assignee directory.
      const [u, subj] = await Promise.all([
        safe(api.get('/crm/users')),
        safe(api.get('/crm/tasks/subjects')),
      ]);
      if (cancelled) return;
      setUsers(u.users || []);
      setLeads(subj.leads || []);
      setAccounts(subj.accounts || []);
      setProjects(subj.projects || []);
      setContacts(subj.contacts || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Guard against out-of-order responses: a slow earlier fetch must not overwrite a
  // newer filter's result. Only the latest request applies its result.
  const reqId = useRef(0);
  const loadTasks = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter });
      const owner = ownerFilter === 'me' ? me : ownerFilter === 'all' ? null : ownerFilter;
      if (owner) params.set('owner', owner);
      const r = await api.get(`/crm/tasks?${params.toString()}`);
      if (id !== reqId.current) return;
      setTasks(r.tasks || []); setLoadError(''); setError('');
    } catch (e) { if (id === reqId.current) { setLoadError(e.message); setError(''); } }
    finally { if (id === reqId.current) setLoading(false); }
  }, [statusFilter, ownerFilter, me]);
  // All loads flow through this one effect — mutations bump `refresh` rather than
  // calling a captured loadTasks(), so a reload always reads the CURRENT filters.
  // Cleanup bumps reqId so an in-flight load is superseded (no setState after unmount).
  useEffect(() => { loadTasks(); return () => { reqId.current++; }; }, [loadTasks, refresh]);

  // id → display maps for subject resolution.
  const maps = useMemo(() => ({
    lead: Object.fromEntries(leads.map((l) => [l.id, l])),
    account: Object.fromEntries(accounts.map((a) => [a.id, a])),
    project: Object.fromEntries(projects.map((p) => [p.id, p])),
    contact: Object.fromEntries(contacts.map((c) => [c.id, c])),
  }), [leads, accounts, projects, contacts]);

  const userByEmail = useMemo(
    () => Object.fromEntries(users.map((u) => [u.email, u])), [users]);

  function subjectOf(t) {
    if (t.lead_id && maps.lead[t.lead_id]) {
      const l = maps.lead[t.lead_id];
      return { icon: Users, label: l.name || l.company || 'Lead', to: '/leads' };
    }
    if (t.account_id && maps.account[t.account_id]) {
      return { icon: Building2, label: maps.account[t.account_id].name || 'Account', to: '/accounts' };
    }
    if (t.project_id && maps.project[t.project_id]) {
      return { icon: Crosshair, label: maps.project[t.project_id].project_name || 'Project', to: '/projects' };
    }
    if (t.contact_id && maps.contact[t.contact_id]) {
      return { icon: UserRound, label: maps.contact[t.contact_id].name || 'Contact', to: '/contacts' };
    }
    // Subject row exists but reference data hasn't resolved (e.g. archived) — show a generic chip.
    if (t.lead_id) return { icon: Users, label: 'Lead', to: '/leads' };
    if (t.account_id) return { icon: Building2, label: 'Account', to: '/accounts' };
    if (t.project_id) return { icon: Crosshair, label: 'Project', to: '/projects' };
    if (t.contact_id) return { icon: UserRound, label: 'Contact', to: '/contacts' };
    return null;
  }

  function assigneeLabel(t) {
    const u = userByEmail[t.owner_email];
    return u?.display_name || t.owner_email || 'Unassigned';
  }

  async function setStatus(task, status) {
    try { await api.patch(`/crm/tasks/${task.id}`, { status }); reload(); }
    catch (e) { setError(e.message); }
  }

  async function submitReopen(reason) {
    const t = reopening;
    // Returns '' once re-opened, else why it failed, which the dialog shows because the page banner sits behind it.
    try {
      await api.patch(`/crm/tasks/${t.id}`, { status: 'open', reopen_reason: reason.trim() });
      setReopening(null); reload();
      return '';
    } catch (e) { return e.message || 'Could not re-open the task.'; }
  }

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return tasks;
    return tasks.filter((t) => {
      const subj = subjectOf(t);
      // Search the same assignee name shown in each task row.
      return [t.title, t.body, subj?.label, t.owner_email, assigneeLabel(t)]
        .some((v) => v?.toLowerCase().includes(needle));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, q, maps, userByEmail]);

  const grouped = useMemo(() => {
    if (!GROUPED_FILTERS.includes(statusFilter)) return null;
    const now = new Date();
    const out = { overdue: [], today: [], week: [], later: [], none: [] };
    for (const t of visible) out[bucketOf(t, now)].push(t);
    return out;
  }, [visible, statusFilter]);

  if (loading && !tasks.length) {
    return <div className="p-8 text-[var(--text-secondary)]">Loading tasks…</div>;
  }

  const renderRow = (t) => {
    const subj = subjectOf(t);
    const closed = isClosedTask(t.status);
    const canEdit = editable(t);
    const toggleCls = 'grid h-8 w-8 shrink-0 place-items-center rounded-full sm:mt-0.5 sm:h-5 sm:w-5';
    return (
      <div key={t.id}
        className="flex flex-wrap items-start gap-x-3 gap-y-1.5 border-b border-[var(--border)] px-3 py-3 last:border-0 hover:bg-gray-50 dark:hover:bg-slate-800/50 sm:flex-nowrap sm:px-4">
        {/* complete / reopen toggle (editable only) */}
        {!closed ? (
          canEdit ? (
            <button title="Mark complete" onClick={() => setStatus(t, 'done')}
              className={`${toggleCls} border border-gray-300 text-transparent hover:border-green-500 hover:text-green-500`}>
              <Check size={13} />
            </button>
          ) : <span className={`${toggleCls} border border-gray-200`} />
        ) : (
          canEdit ? (
            <button title="Re-open" onClick={() => setReopening(t)}
              className={`${toggleCls} border border-green-500 bg-green-500 text-white hover:bg-green-600`}>
              {t.status === 'done' ? <Check size={13} /> : <SkipForward size={12} />}
            </button>
          ) : (
            <span className={`${toggleCls} border border-green-500 bg-green-500 text-white`}>
              {t.status === 'done' ? <Check size={13} /> : <SkipForward size={12} />}
            </span>
          )
        )}

        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setEditing(t)}>
          <div className={`text-sm font-medium ${closed ? 'text-[var(--text-secondary)] line-through' : 'text-[var(--text-primary)]'}`}>
            {t.title}
          </div>
          {t.body && <div className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-[var(--text-secondary)]">{t.body}</div>}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[var(--text-secondary)]">
            {subj && (
              <button onClick={(e) => { e.stopPropagation(); navigate(subj.to); }}
                className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 hover:bg-gray-200 dark:bg-slate-700">
                <subj.icon size={11} /> {subj.label} <ExternalLink size={9} />
              </button>
            )}
            <span className="inline-flex items-center gap-1"><UserRound size={11} /> {assigneeLabel(t)}</span>
          </div>
        </div>

        <div className="flex w-full shrink-0 items-center gap-2 pl-11 sm:w-auto sm:pl-0">
          {/* status: quick inline switch among active states (editable), else a badge */}
          {!closed && canEdit ? (
            <select value={t.status} onClick={(e) => e.stopPropagation()} onChange={(e) => setStatus(t, e.target.value)}
              title="Status"
              className={`cursor-pointer rounded-md border-0 py-0.5 pl-1.5 pr-1 text-[11px] font-medium ${STATUS_META[t.status]?.cls || ''}`}>
              {ACTIVE_TASK_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
          ) : (
            <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ${STATUS_META[t.status]?.cls || ''}`}>
              {statusLabel(t.status)}
            </span>
          )}
          {t.due_at && (
            <span className={`text-xs ${bucketOf(t, new Date()) === 'overdue' && !closed ? 'font-medium text-red-600' : 'text-[var(--text-secondary)]'}`}>
              {fmtDue(t.due_at)}
            </span>
          )}
          {!closed && canEdit && (
            <button title="Skip" onClick={() => setStatus(t, 'skipped')}
              className="ml-auto p-2 text-gray-300 hover:text-gray-500 sm:ml-0 sm:p-0"><SkipForward size={14} /></button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Tasks</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {visible.length}{statusFilter === 'all' ? '' : ` ${(STATUS_FILTERS.find((s) => s.value === statusFilter)?.label || '').toLowerCase()}`} task{visible.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className={input} value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
            <option value="all">All assignees</option>
            {me && <option value="me">Me</option>}
            {users.filter((u) => u.email !== me).map((u) => (
              <option key={u.id} value={u.email}>{u.display_name || u.email}</option>
            ))}
          </select>
          <select className={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={`${input} min-w-0 flex-1 sm:flex-none`} />
          <button onClick={() => setEditing('new')}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            <Plus size={16} /> New Task
          </button>
        </div>
      </div>

      {(error || loadError) && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error || loadError}</div>}

      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
        {/* Do not show an empty-state message when loading the list failed. */}
        {visible.length === 0 ? (!loadError && (
          <div className="p-10 text-center text-sm text-[var(--text-secondary)]">
            No tasks here. <button onClick={() => setEditing('new')} className="text-primary underline">Create one</button>.
          </div>
        )) : grouped ? (
          BUCKET_ORDER.filter((k) => grouped[k].length).map((k) => (
            <div key={k}>
              <div className="flex items-center gap-2 border-b border-[var(--border)] bg-gray-50 px-4 py-1.5 dark:bg-slate-800">
                <span className={`text-xs font-semibold uppercase tracking-wide ${BUCKET_META[k].tone}`}>{BUCKET_META[k].label}</span>
                <span className="text-[11px] text-[var(--text-secondary)]">{grouped[k].length}</span>
              </div>
              {grouped[k].map(renderRow)}
            </div>
          ))
        ) : (
          visible.map(renderRow)
        )}
      </div>

      {editing && (
        <TaskModal
          task={editing === 'new' ? null : editing}
          users={users}
          leads={leads}
          accounts={accounts}
          readOnly={editing !== 'new' && !editable(editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {reopening && (
        <ReopenModal task={reopening} onClose={() => setReopening(null)} onSubmit={submitReopen} />
      )}
    </div>
  );
}

// Create (with subject picker) or edit a task. Reuses the POST/PATCH /crm/tasks contract.
// readOnly = the viewer isn't the assignee/creator/admin: show fields + history, no save.
function TaskModal({ task, users, leads, accounts, readOnly = false, onClose, onSaved }) {
  const isNew = !task;
  const [form, setForm] = useState({
    title: task?.title || '',
    body: task?.body || '',
    due_at: task?.due_at ? task.due_at.slice(0, 10) : '',
    assignee_user_id: task?.assignee_user_id || '',
  });
  const [subjectType, setSubjectType] = useState('lead'); // new only: 'lead' | 'account'
  const [subjectId, setSubjectId] = useState('');
  const [subjectQ, setSubjectQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [events, setEvents] = useState([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // History timeline (existing tasks only). Empty pre-migration (events table absent).
  useEffect(() => {
    if (!task) return undefined;
    let alive = true;
    api.get(`/crm/tasks/${task.id}/events`).then((r) => { if (alive) setEvents(r.events || []); }).catch(() => {});
    return () => { alive = false; };
  }, [task]);

  // The pick stays listed while the search would hide it.
  const subjectOptions = useMemo(
    () => subjectChoices({ type: subjectType, query: subjectQ, leads, accounts, pickedId: subjectId }),
    [subjectType, subjectQ, leads, accounts, subjectId],
  );

  async function submit(e) {
    e.preventDefault();
    if (readOnly) return;
    if (!form.title.trim()) { setErr('Title required'); return; }
    if (isNew && !subjectId) { setErr('Pick a lead or account to link this task to'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/crm/tasks', {
          title: form.title.trim(),
          body: form.body || null,
          due_at: form.due_at || null,
          assignee_user_id: form.assignee_user_id || null,
          lead_id: subjectType === 'lead' ? subjectId : null,
          account_id: subjectType === 'account' ? subjectId : null,
        });
      } else {
        await api.patch(`/crm/tasks/${task.id}`, {
          title: form.title.trim(),
          body: form.body || null,
          due_at: form.due_at || null,
          assignee_user_id: form.assignee_user_id || undefined, // falsy is ignored server-side
        });
      }
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[var(--text-primary)]">{isNew ? 'New Task' : readOnly ? 'Task' : 'Edit Task'}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        {readOnly && <p className="rounded bg-gray-50 px-3 py-2 text-xs text-[var(--text-secondary)] dark:bg-slate-800">View only — only the assignee, creator, or an admin can edit this task.</p>}

        {isNew && (
          <div className="space-y-1.5">
            <span className="block text-xs font-medium text-[var(--text-secondary)]">Linked to *</span>
            <div className="flex gap-2">
              {['lead', 'account'].map((tp) => (
                <button type="button" key={tp} onClick={() => { setSubjectType(tp); setSubjectId(''); }}
                  className={`rounded-lg border px-3 py-1.5 text-sm capitalize ${subjectType === tp ? 'border-primary bg-primary/10 text-primary' : 'border-[var(--border)] text-[var(--text-secondary)]'}`}>
                  {tp}
                </button>
              ))}
            </div>
            <input className={input + ' w-full'} placeholder={`Search ${subjectType}s…`} value={subjectQ} onChange={(e) => setSubjectQ(e.target.value)} />
            <select className={input + ' w-full'} value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
              <option value="">— select a {subjectType} —</option>
              {subjectOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        )}

        <label className="block text-xs font-medium text-[var(--text-secondary)]">Title *
          <input className={input + ' mt-1 w-full'} value={form.title} onChange={set('title')} disabled={readOnly} />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Notes
          <textarea rows={3} className={input + ' mt-1 w-full'} value={form.body} onChange={set('body')} disabled={readOnly} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Due date
            <input type="date" className={input + ' mt-1 w-full'} value={form.due_at} onChange={set('due_at')} disabled={readOnly} />
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Assign to
            <select className={input + ' mt-1 w-full'} value={form.assignee_user_id} onChange={set('assignee_user_id')} disabled={readOnly}>
              <option value="">{isNew ? '— Me (default) —' : '— Keep current —'}</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
            </select>
          </label>
        </div>

        {!isNew && events.length > 0 && (
          <div className="border-t border-[var(--border)] pt-2">
            <p className="mb-1 text-xs font-medium text-[var(--text-secondary)]">History</p>
            <ul className="max-h-32 space-y-1 overflow-y-auto pr-1">
              {events.map((ev) => (
                <li key={ev.id} className="text-[11px] text-[var(--text-secondary)]">
                  <span className="text-[var(--text-primary)]">{describeEvent(ev)}</span>
                  {' · '}{ev.actor_email || 'system'}
                  {' · '}{new Date(ev.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button type="submit" disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {saving ? 'Saving…' : isNew ? 'Create task' : 'Save'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// Re-opening a completed/skipped task requires a reason (blocking) — Chase's spec point.
function ReopenModal({ task, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <div className="flex items-center gap-2 text-[var(--text-primary)]"><RotateCcw size={16} /><h3 className="font-semibold">Re-open task</h3></div>
        <p className="text-xs text-[var(--text-secondary)]">“{task.title}” — add a reason so the history is clear.</p>
        <textarea autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this being re-opened?" className={input + ' w-full'} />
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button disabled={!reason.trim() || saving}
            onClick={async () => { setSaving(true); setErr(''); const why = await onSubmit(reason); if (why) { setErr(why); setSaving(false); } }}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? 'Re-opening…' : 'Re-open'}
          </button>
        </div>
      </div>
    </div>
  );
}
